import type { ApiClient } from './client';
import { route } from './route';
import { AITHORA_PREFIX } from './prefix';
import type {
  AlertRule,
  AtlasActivity,
  AtlasAnswer,
  CaptureSummary,
  PulseAnomalies,
  PulseEnvelope,
  PulseFacilities,
  PulseIncident,
  PulseIncidents,
  PulseSlo,
  PulseV3Facilities,
} from './pulse-types';
import type { FrameworksResponse } from './overview-types';

const A = AITHORA_PREFIX;

// ── /api/pulse (envelope, no `real` flag) ───────────────────────────────────

/** GET /api/pulse/facilities: per-facility signals (sensor, companion, escalation...). */
export const pulseFacilities = route<PulseEnvelope<PulseFacilities>>()('GET', '/api/pulse/facilities');
/** GET /api/pulse/slo: objectives, attainment and budget burn. */
export const pulseSlo = route<PulseEnvelope<PulseSlo>>()('GET', '/api/pulse/slo');
/** GET /api/pulse/incidents?status=&windowDays= */
export const pulseIncidents = route<PulseEnvelope<PulseIncidents>, { status?: 'open' | 'acknowledged' | 'resolved'; windowDays?: number }>()(
  'GET', '/api/pulse/incidents');
/** POST /api/pulse/incidents/:id/ack: acknowledging halts escalation. */
export const pulseIncidentAck = route<PulseEnvelope<PulseIncident>, void, { confirm: true }>()('POST', '/api/pulse/incidents/:id/ack');
/** GET /api/pulse/anomalies: each facility against its own baseline. */
export const pulseAnomalies = route<PulseEnvelope<PulseAnomalies>>()('GET', '/api/pulse/anomalies');
/** GET /api/pulse/alert-rules */
export const pulseAlertRules = route<PulseEnvelope<AlertRule[]>>()('GET', '/api/pulse/alert-rules');

// ── /api/aithora ────────────────────────────────────────────────────────────

/** GET /pulse-v3/facilities: building cards with their links (own building for a Facility Admin). */
export const pulseV3Facilities = route<PulseV3Facilities>()('GET', `${A}/pulse-v3/facilities`, { honest: true });
/** GET /capture/summary?facility=&period=: measured, recoverable and (modeled) opportunity. */
export const captureSummary = route<CaptureSummary, { facility?: string; facilities?: string; period?: string }>()('GET', `${A}/capture/summary`, { honest: true });
/** GET /atlas/activity?limit=: what Atlas did, newest first. */
export const atlasActivity = route<AtlasActivity, { limit?: number; cursor?: string }>()('GET', `${A}/atlas/activity`, { honest: true });

/**
 * POST /api/atlas/ask streams Server-Sent Events. This reads the stream and calls
 * onChunk for each answer-chunk, then resolves with the final `answer` event.
 */
export async function atlasAsk(
  client: ApiClient,
  body: { question: string; history?: { role: 'user' | 'assistant'; content: string }[] },
  opts: { onChunk?: (text: string) => void; signal?: AbortSignal } = {},
): Promise<AtlasAnswer> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json', Accept: 'text/event-stream' };
  const res = await client.raw('/api/atlas/ask', { method: 'POST', headers, body: JSON.stringify(body), signal: opts.signal });
  if (!res.ok) {
    let msg = `Atlas could not answer (${res.status}).`;
    try {
      const j = (await res.json()) as { error?: string };
      if (j?.error) msg = j.error;
    } catch {
      /* not JSON */
    }
    const err = new Error(msg) as Error & { status: number; requestId: string | null };
    err.status = res.status;
    err.requestId = res.headers.get('x-request-id');
    throw err;
  }
  const text = await res.text();
  let answer: AtlasAnswer | null = null;
  let failure: string | null = null;
  for (const block of text.split(/\n\n+/)) {
    const ev = /^event: (.+)$/m.exec(block)?.[1]?.trim();
    const data = /^data: (.*)$/m.exec(block)?.[1];
    if (!ev || data === undefined) continue;
    let parsed: unknown;
    try {
      parsed = JSON.parse(data);
    } catch {
      continue;
    }
    if (ev === 'answer-chunk') opts.onChunk?.(String((parsed as { text?: unknown }).text ?? ''));
    else if (ev === 'answer') answer = parsed as AtlasAnswer;
    else if (ev === 'error') failure = String((parsed as { error?: unknown }).error ?? 'Atlas stopped part way.');
  }
  if (failure && !answer) throw new Error(failure);
  if (!answer) throw new Error('Atlas sent no answer.');
  return answer;
}

/** GET /api/frameworks: the compliance frameworks and, once served, how far each is mapped. */
export const frameworks = route<FrameworksResponse>()('GET', '/api/frameworks');

export const pulseApi = {
  frameworks,
  pulseFacilities,
  pulseSlo,
  pulseIncidents,
  pulseIncidentAck,
  pulseAnomalies,
  pulseAlertRules,
  pulseV3Facilities,
  captureSummary,
  atlasActivity,
} as const;
